import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import useLocation from 'hooks/useLocation';
import { useMemo, useState } from 'react';
import {
  useGetAirQualityForecastQuery,
  useGetAirQualityObservationQuery,
} from 'services/weather/weather.api';
import { getAirQualityForecastStations } from '../../features/AirQuality/utils/airQuality.utils';
import AirQualityMeter from './components/AirQualityMeter/AirQualityMeter';
import AirQualityReportList from './components/AirQualityReportList/AirQualityReportList';
import AirQualityStationSelect from './components/AirQualityStationSelect/AirQualityStationSelect';
import { getAirQualityWidgetConfig } from './config/airQualityWidget.config';
import {
  getAirQualityForecastReportItems,
  getAirQualityObservationReportItems,
  getAirQualityObservationStations,
  getSelectedAirQualityStation,
} from './utils/airQualityWidget.utils';
import './assets/airQualityWidget.scss';

const AirQualityWidget = ({ widgetType, layout = '1x1', dimensions }) => {
  const { locations } = useLocation();
  const widgetConfig = getAirQualityWidgetConfig(widgetType);
  const isForecast = widgetConfig.mode === 'forecast';
  const [selectedStationName, setSelectedStationName] = useState('');
  const airQualityObservations = useGetAirQualityObservationQuery(locations.effectiveLocation, {
    skip: isForecast,
  });
  const airQualityForecast = useGetAirQualityForecastQuery(locations.effectiveLocation, {
    skip: !isForecast,
  });
  const activeQuery = isForecast ? airQualityForecast : airQualityObservations;
  const isMeterLayout = layout === '1x1';

  const activeQueryData = useMemo(() => activeQuery?.data ?? [], [activeQuery]);

  const stations = useMemo(
    () =>
      isForecast
        ? getAirQualityForecastStations(activeQueryData)
        : getAirQualityObservationStations(activeQueryData),
    [activeQueryData, isForecast],
  );

  const selectedStation = getSelectedAirQualityStation(stations, selectedStationName);
  const reports = isForecast
    ? getAirQualityForecastReportItems(selectedStation)
    : getAirQualityObservationReportItems(stations);

  if (activeQuery.isError) {
    return (
      <ErrorMessage
        message={activeQuery.error?.message ?? 'Unable to load air quality information.'}
        retryRoute={widgetConfig.route}
        onRetry={activeQuery.refetch}
      />
    );
  }

  if (activeQuery.isLoading) {
    return <WidgetLoadingAnimation widgetName="Air Quality" dimensions={dimensions} />;
  }

  return (
    <section
      className="air-quality-widget d-flex flex-column h-100 overflow-hidden"
      data-layout={layout}
      data-mode={widgetConfig.mode}
      aria-label={widgetConfig.ariaLabel}
    >
      <WidgetTimeUpdate
        route={widgetConfig.route}
        updatedAt={activeQuery.fulfilledTimeStamp ?? null}
      />

      {!stations.length ? (
        <p className="air-quality-widget-empty p-3 mb-0" role="status">
          No air quality data found.
        </p>
      ) : (
        <div className="air-quality-widget-content d-flex flex-column flex-fill gap-2 overflow-hidden">
          {(isForecast || isMeterLayout) && (
            <AirQualityStationSelect
              stations={stations}
              selectedStationName={selectedStation?.stationName ?? ''}
              onSelect={setSelectedStationName}
            />
          )}

          {isMeterLayout ? (
            <AirQualityMeter report={selectedStation?.reports?.[0]} />
          ) : (
            <div className="air-quality-report-scroll overflow-y-auto">
              <AirQualityReportList reports={reports} layout={layout} />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AirQualityWidget;
